import { useCallback, useEffect, useState } from 'react';

export interface UseSeriesVisibilityOptions {
  defaultHiddenSeriesIds?: string[];
  onSeriesToggle?: (seriesId: string, wasHidden: boolean) => void;
}

export interface UseSeriesVisibilityResult {
  hiddenSeriesIds: Set<string>;
  removedSeriesIds: Set<string>;
  handleSeriesToggle: (seriesId: string, wasHidden: boolean) => void;
  removeSeriesIds: (ids: string[]) => void;
}

export const useSeriesVisibility = ({
  defaultHiddenSeriesIds = [],
  onSeriesToggle,
}: UseSeriesVisibilityOptions): UseSeriesVisibilityResult => {
  const defaultHiddenKey = defaultHiddenSeriesIds.join('\u0000');
  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<Set<string>>(
    () => new Set(defaultHiddenSeriesIds)
  );
  const [removedSeriesIds, setRemovedSeriesIds] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    setHiddenSeriesIds(new Set(defaultHiddenSeriesIds));
  }, [defaultHiddenKey]);

  const handleSeriesToggle = useCallback(
    (seriesId: string, wasHidden: boolean) => {
      setHiddenSeriesIds(prev => {
        const next = new Set(prev);

        if (wasHidden) {
          next.delete(seriesId);
        } else {
          next.add(seriesId);
        }

        return next;
      });
      onSeriesToggle?.(seriesId, wasHidden);
    },
    [onSeriesToggle]
  );

  const removeSeriesIds = useCallback((ids: string[]) => {
    if (ids.length === 0) {
      return;
    }

    setRemovedSeriesIds(prev => {
      const next = new Set(prev);

      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  return {
    hiddenSeriesIds,
    removedSeriesIds,
    handleSeriesToggle,
    removeSeriesIds,
  };
};
